import { useMutation, useQuery } from '@tanstack/react-query';
import { Loader2, Play } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { partnerApi, type PartnerUser, type SchemaLookupToolDefinition } from '../../lib/api';
import { LookupToolTestResult } from './LookupToolTestResult';
import { ScopeHelp } from './ScopeHelp';
import { BASE_SCOPE, TOOL_SCOPE_OPTIONS, VERIFIED_SCOPE, type ToolScope } from './tool-scopes';
import { tenantPickerQuery } from './tool-test-tenants';
import { MAX_HISTORY, ToolTestHistory, type ToolTestRunRecord } from './ToolTestHistory';
import styles from './ToolTestPanel.module.css';

const MIN_SEARCH_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_LENGTH_HINT = `Enter at least ${MIN_SEARCH_LENGTH} characters to search.`;

interface LookupToolTestPanelProps {
  tool: SchemaLookupToolDefinition;
}

const customerLabel = (user: PartnerUser): string => {
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ');
  const identifiers = [user.email, user.phone].filter(Boolean);
  const externalIdLabel = user.externalId ? `External ID: ${user.externalId}` : '';
  const recognizableName = fullName || identifiers[0] || user.externalId;
  const context = [...identifiers, externalIdLabel]
    .filter(identifier => Boolean(identifier) && identifier !== recognizableName)
    .join(' · ');
  return context ? `${recognizableName} — ${context}` : recognizableName;
};

export const LookupToolTestPanel = ({ tool }: LookupToolTestPanelProps) => {
  const [businessId, setBusinessId] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState<PartnerUser | null>(null);
  const [scope, setScope] = useState<ToolScope>(
    tool.required_scope === VERIFIED_SCOPE ? VERIFIED_SCOPE : BASE_SCOPE,
  );
  const [runs, setRuns] = useState<ToolTestRunRecord[]>([]);
  const [selectedRunId, setSelectedRunId] = useState<number | null>(null);
  const nextRunId = useRef(0);

  const tenantsQuery = useQuery(tenantPickerQuery);
  const tenants = tenantsQuery.data?.data.results ?? [];

  const trimmedSearch = search.trim();
  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearch(trimmedSearch), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [trimmedSearch]);

  const usersQuery = useQuery({
    queryKey: ['partner-users', { businessId, search: debouncedSearch, status: 'active' }],
    queryFn: () =>
      partnerApi.getPartnerUsers({
        tenantId: businessId,
        search: debouncedSearch,
        status: 'active',
        limit: 20,
      }),
    enabled: Boolean(businessId) && debouncedSearch.length >= MIN_SEARCH_LENGTH,
  });
  const users = (usersQuery.data?.data.results ?? []).filter(
    user => user.status === 'active' && !user.deactivatedAt,
  );

  const testMutation = useMutation({
    mutationFn: () =>
      partnerApi.testTool(tool.id, {
        businessId,
        partnerUserId: selectedUser?.id,
        scope,
        args: {},
      }),
    onSuccess: ({ result }) => {
      nextRunId.current += 1;
      const record: ToolTestRunRecord = { id: nextRunId.current, at: new Date(), result };
      setRuns(previous => [record, ...previous].slice(0, MAX_HISTORY));
      setSelectedRunId(record.id);
    },
  });

  const selectedRun = runs.find(run => run.id === selectedRunId);
  const scopeInsufficient = tool.required_scope === VERIFIED_SCOPE && scope === BASE_SCOPE;
  const pendingMessage = selectedUser
    ? `Looking up synced records for ${customerLabel(selectedUser)}…`
    : '';

  return (
    <fieldset className={styles.panel}>
      <legend className={styles.legend}>Test lookup</legend>
      <p className={styles.description}>
        Select a tenant and a real, active customer. The lookup uses only the selected customer identity;
        schema relationship, filters, output fields, and args remain fixed.
      </p>
      <section className={styles.fields}>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>Tenant</span>
          <select
            value={businessId}
            onChange={event => {
              setBusinessId(event.target.value);
              setSearch('');
              setDebouncedSearch('');
              setSelectedUser(null);
            }}
            className={styles.selectFull}>
            <option value="">Pick a tenant…</option>
            {tenants.map(tenant => (
              <option key={tenant.id} value={tenant.id}>
                {tenant.name}
              </option>
            ))}
          </select>
        </label>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>Search active customers</span>
          <input
            type="search"
            value={search}
            onChange={event => {
              setSearch(event.target.value);
              setSelectedUser(null);
            }}
            disabled={!businessId}
            className={styles.input}
            placeholder="Name, email, phone, or external ID"
          />
          {businessId && trimmedSearch.length < MIN_SEARCH_LENGTH && (
            <span className={styles.neutralHint}>{SEARCH_LENGTH_HINT}</span>
          )}
        </label>

        {usersQuery.isFetching && <span className={styles.neutralHint}>Searching customers…</span>}
        {usersQuery.isSuccess && debouncedSearch === trimmedSearch && (
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Identified customer</span>
            <select
              value={selectedUser?.id ?? ''}
              onChange={event =>
                setSelectedUser(users.find(user => user.id === event.target.value) ?? null)
              }
              className={styles.selectFull}>
              <option value="">Choose a customer…</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>
                  {customerLabel(user)}
                </option>
              ))}
            </select>
            {users.length === 0 && (
              <span className={styles.neutralHint}>No active customers matched this search.</span>
            )}
          </label>
        )}

        <div className={styles.field}>
          <span className={styles.fieldLabelRow}>
            <label htmlFor="lookup-test-scope" className={styles.fieldLabel}>
              Scope (simulate)
            </label>
            <ScopeHelp />
          </span>
          <select
            id="lookup-test-scope"
            value={scope}
            onChange={event => setScope(event.target.value as ToolScope)}
            className={styles.select}>
            {TOOL_SCOPE_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {scopeInsufficient && (
            <span className={styles.hint}>
              This run simulates an unverified caller, but the tool requires a verified caller.
            </span>
          )}
        </div>

        <div className={styles.actions}>
          <button
            type="button"
            onClick={() => testMutation.mutate()}
            disabled={!businessId || !selectedUser || testMutation.isPending}
            className={styles.primaryButton}>
            <Play />
            {testMutation.isPending ? 'Running…' : 'Run lookup'}
          </button>
          {testMutation.error && <span className={styles.errorText}>{testMutation.error.message}</span>}
        </div>

        {testMutation.isPending && pendingMessage && (
          <div className={styles.pending}>
            <Loader2 className={styles.spinner} />
            {pendingMessage}
          </div>
        )}

        {!testMutation.isPending && selectedRun && <LookupToolTestResult result={selectedRun.result} />}

        <ToolTestHistory runs={runs} selectedId={selectedRunId} onSelect={setSelectedRunId} />
      </section>
    </fieldset>
  );
};
